import { ClientDateHandler } from '../clientDateHandler/ClientDateHandler';

export class CommonValidation {
    static isValidValue(value: null | undefined | string) {
        const isValid = !(
            value === null ||
            value === undefined ||
            value === ''
        );
        return isValid;
    }

    static isNotEmpty(value: null | undefined | string | Date | number) {
        const notEmpty = !CommonValidation.isEmpty(value);
        return notEmpty;
    }

    static isEmpty(value: null | undefined | string | Date | number) {
        const isEmpty = value === null || value === undefined || value === '';
        return isEmpty;
    }

    static isValidDecimal(value: string) {
        const decimalExpression = /^-{0,1}\d*\.{0,1}\d+$/;
        const isValid = decimalExpression.test(value);
        return isValid;
    }

    static isDecimalWithinRange(valueText: number | string) {
        let withinRange = true;
        let value = 0;

        if (typeof valueText === 'string') {
            value = parseFloat(valueText);
        } else {
            value = valueText;
        }

        if (isNaN(value)) {
            return false;
        }

        if (value > 99999999.0 || value < 0) withinRange = false;

        return withinRange;
    }

    static isInteger(value: number | string) {
        if (value == null) return false;

        const noDecimals = value.toString().split('.').length == 1;
        const isValid = CommonValidation.isValidNumeric(value) && noDecimals;

        return isValid;
    }

    static isValidNumeric(value: null | undefined | string | number) {
        if (typeof value === 'string') {
            value = Number(value);
        }

        const isValid = !isNaN(value);
        return isValid;
    }

    static isValidAlphabetic(value: string) {
        const alphabeticExpression = /^[ a-zA-Z\.]*$/; //eslint-disable-line
        const isValid = alphabeticExpression.test(value);
        return isValid;
    }

    static isValidAlphaNumeric(value: string) {
        const alphaNumericExpression = /^[ a-zA-Z0-9\.]*$/; //eslint-disable-line
        const isValid = alphaNumericExpression.test(value);
        return isValid;
    }

    static isValidModel(value: string) {
        const alphaNumericExpression = /^[ a-zA-Z0-9()\.]*$/; //eslint-disable-line
        const isValid = alphaNumericExpression.test(value);
        return isValid;
    }

    static isValidEmail(value: string) {
        const emailExpression = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/; //eslint-disable-line

        const isValid = emailExpression.test(value);
        return isValid;
    }

    static isValidPhone(value: string) {
        const phoneExpression = /^[\+]?[(]?[0-9]{2,5}[)]?[-\s\.]?[0-9]{6,7}$/; //eslint-disable-line
        const isValid = phoneExpression.test(value);
        return isValid;
    }

    static isStartDatePriorToEndDate(startDate?: Date, endDate?: Date) {
        if (!startDate || !endDate) return false;

        return startDate < endDate;
    }

    static isStartDateEqualOrPriorToEndDate(startDate?: Date, endDate?: Date) {
        if (!startDate || !endDate) return false;

        return startDate <= endDate;
    }

    static isValidLength(value: string, maxLength: number) {
        if (CommonValidation.isEmpty(value)) return true;

        const isValid = value.length <= maxLength;
        return isValid;
    }

    static getJsonDate(jsonDate?: string) {
        if (!jsonDate) return new Date();

        const cdh = new ClientDateHandler();
        cdh.parseJSON(jsonDate);
        const dateValue = cdh.getLocalDate();
        return dateValue;
    }

    static getNullDateJson(isUtc = false) {
        const cdh = new ClientDateHandler();
        cdh.setDateValue(null, isUtc);
        const nullDateJson = cdh.getJSON();
        return nullDateJson;
    }

    static isDateNull(date: string | Date | undefined, isJson: boolean) {
        if (date == undefined) return true;

        if (isJson) {
            const nullDateJson = this.getNullDateJson(false);
            const nullDateJsonUtc = this.getNullDateJson(true);
            const dateIsNull =
                date === nullDateJson || date === nullDateJsonUtc;
            console.log('Null date JSON: ', nullDateJson);
            console.log('PAssed date: ', date);
            console.log('Date is null:', dateIsNull);
            return dateIsNull;
        } else {
            return CommonValidation.isEmpty(date);
        }
    }

    static isValidDate = (
        value: string | Date | undefined,
        isJson: boolean
    ) => {
        //null dates are valid dates - should use isDateNull in the validation if checking for mandatory dates
        if (CommonValidation.isDateNull(value, isJson)) {
            return true;
        }

        if (value === undefined) return false;

        const sqlMinDate = new Date('1000-01-01');
        const sqlMaxDate = new Date('9999-12-31');

        let dateValue: string | Date | undefined;
        if (isJson) {
            let isValidDate = false;
            const parsedDate = JSON.parse(value as string);
            const parsedDateValue: string = parsedDate.value;
            isValidDate =
                new Date(parsedDateValue) >= sqlMinDate &&
                new Date(parsedDateValue) <= sqlMaxDate;

            return isValidDate;
        } else {
            const dateVal = dateValue as Date;
            return dateVal >= sqlMinDate && dateVal <= sqlMaxDate;
        }
    };

    static daysPassed = (dateOfIncident: string): number => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        const incidentDate = CommonValidation.getJsonDate(dateOfIncident);

        const diff = Math.abs(currentDate.getTime() - incidentDate.getTime());
        const daysDifference = Math.ceil(diff / (1000 * 3600 * 24));

        return daysDifference;
    };

    static dateDifference = (date1: string, date2: string): number => {
        const date1Dt = CommonValidation.getJsonDate(date1);
        date1Dt.setHours(0, 0, 0, 0);
        const date2Dt = CommonValidation.getJsonDate(date2);
        date2Dt.setHours(0, 0, 0, 0);

        const diff = date1Dt.getTime() - date2Dt.getTime();
        const daysDifference = Math.ceil(diff / (1000 * 3600 * 24));

        return daysDifference;
    };

    static getDate = (dateValue: string, countryCode: string): string => {
        const date = CommonValidation.getJsonDate(dateValue);

        const dd = date.getDate();
        const mm = date.getMonth() + 1;
        let day = dd.toString();
        let month = mm.toString();
        const yyyy = date.getFullYear();
        if (dd < 10) {
            day = '0' + dd;
        }
        if (mm < 10) {
            month = '0' + mm;
        }
        if (countryCode !== 'us')
            return (dateValue = day + '/' + month + '/' + yyyy);
        else return (dateValue = month + '/' + day + '/' + yyyy);
    };
}
